import './style.scss';
import { imageLazyLoading } from '../../scripts/functions/imageLazyLoading';
import { animations } from '../../scripts/general/animations';
import { gsap } from 'gsap';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';

gsap.registerPlugin(DrawSVGPlugin);

const heroBlock = async (block) => {
  const mq = window.matchMedia('(min-width: 992px)');

  const animationContainer = block.querySelector('.cover-images');
  const bottle = block.querySelector('.bottle');              // <video> wrapper (desktop only)
  const bottleVideo = bottle?.querySelector('video');
  const staticBottle = block.querySelector('.static-bottle'); // <img> (mobile/tablet)

  const inkVideoContainer = block.querySelector('#ink-video-hero'); // wrapper with <video>
  const inkVideo = inkVideoContainer?.querySelector('video');

  const drawing = block.querySelector('.drawing');

  const playbackRate = 0.85;
  const bottleRollRatio = 0.65;
  const tl = gsap.timeline({ paused: true });

  // --- helpers -------------------------------

  // Move data-src / data-srcset onto real attrs before load
  const hydrateMediaSources = (mediaEl) => {
    if (!mediaEl) return;
    // <video> can have <source> children
    mediaEl.querySelectorAll('source').forEach((s) => {
      if (s.dataset.src) s.src = s.dataset.src;
      if (s.dataset.srcset) s.srcset = s.dataset.srcset;
      // type stays as is
    });
    // Some browsers require calling load() after changing sources
    if (typeof mediaEl.load === 'function') mediaEl.load();
  };

  const hydrateImg = (img) => {
    if (!img) return;
    if (img.dataset.src) img.src = img.dataset.src;
    if (img.dataset.srcset) img.srcset = img.dataset.srcset;
    if (img.dataset.sizes) img.sizes = img.dataset.sizes;
  };

  const waitForVideo = (video) =>
    new Promise((resolve) => {
      if (!video) return resolve();
      if (video.readyState >= 3) return resolve();
      video.addEventListener('loadeddata', resolve, { once: true });
      video.addEventListener('error', resolve, { once: true });
    });

  const showStaticOnly = () => {
    // Mobile/tablet: ensure videos are stopped/hidden
    if (bottleVideo) {
      bottleVideo.pause?.();
      bottleVideo.currentTime = 0;
    }
    if (inkVideo) {
      inkVideo.pause?.();
      inkVideo.currentTime = 0;
    }
    if (inkVideoContainer?.parentElement === document.body) {
      // If previously appended on desktop, put it back in block or hide it
      document.body.removeChild(inkVideoContainer);
    }

    // Hydrate and show the static image
    hydrateImg(staticBottle);
    if (animationContainer) animationContainer.style.opacity = 1;

    // Keep your existing hooks
    animations(block);
    imageLazyLoading(block);
  };

  const runDesktopAnimation = async () => {
    // Prepare/hydrate all media sources for desktop
    hydrateMediaSources(bottleVideo);
    hydrateMediaSources(inkVideo);

    // iOS autoplay friendliness
    if (bottleVideo) {
      bottleVideo.muted = true; // ensure autoplay works cross-browser
      bottleVideo.playsInline = true;
      bottleVideo.playbackRate = playbackRate;
    }
    if (inkVideo) {
      inkVideo.muted = true;
      inkVideo.playsInline = true;
      inkVideo.playbackRate = 0.5;
      inkVideo.currentTime = 2.1;
    }

    // Wait until both videos can play
    await Promise.all([waitForVideo(bottleVideo), waitForVideo(inkVideo)]);

    window.scrollTo(0, 0);
    if (animationContainer) animationContainer.style.opacity = 1;

    if (bottleVideo) {
      bottleVideo.currentTime = 0;
    }

    // Put ink video container on top of layout (per your original behavior)
    console.log('will try to append ink', inkVideoContainer , inkVideoContainer.parentNode !== document.body);
    if (inkVideoContainer && inkVideoContainer.parentNode !== document.body) {
      document.body.append(inkVideoContainer);
    }

    // Build timeline
    tl.clear();
    tl.set(bottle, { yPercent: -50, y: 0 })
      .to(bottle, {
        x: () => -window.innerWidth * (window.innerWidth > 900 ? 0.63 : 0.87),
        duration:
          (bottleVideo?.duration ? bottleVideo.duration : 3) *
          bottleRollRatio /
          playbackRate,
        ease: 'power1.out',
      })
      .to(
        inkVideoContainer,
        {
          opacity: 1,
          scale: 1,
          duration: 3,
          onStart() {
            inkVideo?.play?.();
          },
        },
        '+=1'
      )
      .from(drawing, { opacity: 0, duration: 7 }, '<1');

    // Start
    bottleVideo?.play?.();
    tl.play(0);

    animations(block);
    imageLazyLoading(block);
  };

  // --- main branch by breakpoint ----------------
  if (!mq.matches) {
    // < 992px: static image only
    showStaticOnly();
    return;
  }

  // ≥ 992px: video flow
  await runDesktopAnimation();

  // (Optional) If you want to react to live resizing across the 992px breakpoint:
  // Debounced listener that switches modes when crossing the boundary
  let resizeRAF;
  const onResize = () => {
    if (resizeRAF) cancelAnimationFrame(resizeRAF);
    resizeRAF = requestAnimationFrame(() => {
      const desktopNow = window.matchMedia('(min-width: 992px)').matches;
      // If user shrinks below 992 while video is running, fall back to static
      if (!desktopNow) {
        tl.kill();
        showStaticOnly();
        window.removeEventListener('resize', onResize);
      }
    });
  };
  window.addEventListener('resize', onResize);
};

export default heroBlock;
